import { createSelector } from "@reduxjs/toolkit";

import { getDesignerNodeDataByIdSelector } from "@skilitics/nautilus-core-designer-data";

import { getNodeProfileByIdCreate, getNodeProfileForRoleCreate } from "../nodePublicApi.ts";
import { useNautilusSelector } from "../shared.ts";
import { getManifestSelector } from "./appConfigSelectors.ts";
import { getMonolingualDesignerNodeDataSelector, monolingualizeConfigSelector } from "./nodeConfigSelectors.ts";
import { getNodeRoleSelector } from "./nodeRoleSelectors.ts";

export const getNodeProfileByIdSelector = createSelector(
  getManifestSelector,
  getMonolingualDesignerNodeDataSelector,
  getDesignerNodeDataByIdSelector,
  monolingualizeConfigSelector,
  (getManifest, getMonolingualDesignerNodeData, getDesignerNodeDataById, monolingualizeConfig) =>
    getNodeProfileByIdCreate({
      getManifest,
      getMonolingualDesignerNodeData,
      getDesignerNodeDataById,
      monolingualizeConfig,
    }),
);

/**
 * Resolve another node's public API ("profile") by id — the sanctioned way to read
 * from a node you don't own, replacing reaching into its `config`.
 */
export const useNodeProfileById = () => useNautilusSelector(getNodeProfileByIdSelector);

export const getNodeProfileForRoleSelector = createSelector(
  getNodeProfileByIdSelector,
  getNodeRoleSelector,
  getDesignerNodeDataByIdSelector,
  (getNodeProfileById, getNodeRole, getDesignerNodeDataById) =>
    getNodeProfileForRoleCreate({
      getNodeProfileById,
      getNodeRoleById: (id) => {
        const { typeId, role } = getDesignerNodeDataById(id);
        return getNodeRole({ id, typeId, role });
      },
    }),
);

/**
 * Role-typed by-id profile access — the designer-role counterpart of
 * `useNodeProfileById`: `profileForRole<SceneDesignerRole>(id, "scene")` returns
 * `SceneRoleProfile | undefined`. Guarded by the node's effective runtime role
 * string only (designer-roles are types-only).
 */
export const useNodeProfileForRole = () => useNautilusSelector(getNodeProfileForRoleSelector);
